import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import useForm from 'react-hook-form';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNull } from 'lodash';
import PropTypes from 'prop-types';
import { getCurrencies } from '../../../../redux/modules/currencies';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import FormSection from '../../../../components/FormComponents/FormSection';
import DateInputFieldWithValidation from '../../../../components/ValidationFormElements/DateInputFieldWithValidation';
import TextInputFieldWithValidation from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import SelectWithValidation from '../../../../components/ValidationFormElements/SelectWithValidation';
import ExistingContracts from '../../../../components/ClaimFormComponents/ExistingContracts';
import MainSwitch from '../../../../components/MUI/MainSwitch';
import Validation from '../../../../components/Validation/Validation';
import TextAreaField from '../../../../components/FormComponents/TextAreaField';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import { bindPolicies, handleUpdateDisabledInContracts, selectPoliciByDefault, sortContractByIsDisabled } from '../../../../helpers/claims';
import { NON_DIGITS_PATTERN } from '../../../../constants/patterns';

import useStyles from '../../useStyles';
import styles from '../steps.module.scss';

import AccidentInfoIcon from '../../../../img/formSectionIcons/accidentInfo.svg';
import { CLAIM_TYPES } from '../../../../constants';

const AccidentInfoStep = ({
  formLanguage,
  claim,
  setStepNavigationBox,
  step,
  goNext,
  goPrevious,
  updateForm,
  serviceCenterComponent,
  accidentAddressComponent,
  accidentTimeComponent,
  requiredFields,
  validationSchema,
  countries,
  claimType,
}) => {
  const { accidentInfo } = claim;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();
  const isDeviceLgSize = useDeviceResize('lg');
  const { currencies } = useSelector((store) => store.currenciesReducer, shallowEqual);

  const [accDate, setAccDate] = useState(accidentInfo && accidentInfo.accidentDate);
  const policies = useMemo(() => bindPolicies(accidentInfo, accDate), [accidentInfo, accDate]);
  const [existingContracts, setExistingContracts] = useState(sortContractByIsDisabled(policies));
  const [showContractNotExistError, setShowContractNotExistError] = useState(false);
  const [contract, setContract] = useState((accidentInfo && accidentInfo.contract) || null);
  const [damageAmount, setDamageAmount] = useState(accidentInfo?.damageAmount || 0);
  const [accidentInformation, setAccidentInformation] = useState(
    (accidentInfo && accidentInfo.accidentInformation) || '',
  );
  const [openAccidentInfoBlock, setOpenAccidentInfoBlock] = useState(false);
  const [currency, setCurrency] = useState(accidentInfo && accidentInfo.currency);

  const { handleSubmit, register, unregister, setValue, errors, clearError, getValues } = useForm({
    validationSchema,
    defaultValues: accidentInfo,
    mode: 'onBlur',
  });

  useRegisterValidationFields(requiredFields, register);

  useEffect(() => {
    setValue('formLanguage', formLanguage);
  }, [formLanguage, setValue]);

  useEffect(() => {
    dispatch(getCurrencies());
  }, [dispatch]);

  useEffect(() => {
    if (!openAccidentInfoBlock) {
      setValue('accidentInformation', null, true);
      setAccidentInformation('');
    }
  }, [setValue, openAccidentInfoBlock]);

  useEffect(() => {
    if (!isNull(accidentInfo)) {
      setDamageAmount(accidentInfo.damageAmount);
      setAccDate(accidentInfo.accidentDate);
      setAccidentInformation(accidentInfo.accidentInformation);
      setCurrency(accidentInfo.currency);
      setOpenAccidentInfoBlock(!!accidentInfo.accidentInformation);

      setValue('accidentDate', accidentInfo.accidentDate);
      setValue('contract', accidentInfo.contract);
      setValue('damageAmount', accidentInfo.damageAmount);
      setValue('currency', accidentInfo.currency);
      setValue('accidentInformation', accidentInfo.accidentInformation);
    }
  }, [accidentInfo, setValue]);

  const updateContract = useCallback(
    (existingContract) => {
      setContract(existingContract);
      setValue('contract', existingContract, true);
    },
    [setValue],
  );

  useEffect(() => {
    selectPoliciByDefault(policies, updateContract);
  }, [policies, updateContract]);

  useEffect(() => {
    if (currencies.length) {
      setCurrency(currencies[0]);
      setValue('currency', currencies[0], true);
    }
  }, [currencies, setValue]);

  const updateDamageAmount = (amount) => {
    setDamageAmount(amount);
    setValue('damageAmount', amount, !!amount);
  };

  const updateIsDisabledInContracts = (date = null) => {
    handleUpdateDisabledInContracts(
      date,
      existingContracts,
      setExistingContracts,
      contract,
      updateContract,
      setShowContractNotExistError,
    );
  };

  const updateAccDate = (newDate) => {
    setAccDate(newDate);
    setValue('accidentDate', newDate, true);
    updateIsDisabledInContracts(newDate);
    selectPoliciByDefault(policies, updateContract);
  };

  const updateAccidentInformation = (text) => {
    if (text) {
      register({ name: 'accidentInformation' });

      setValue('accidentInformation', text, true);
    } else {
      unregister('accidentInformation');
    }

    setAccidentInformation(text);
  };

  const handleCurrencyChange = (selectedCurrency) => {
    setCurrency(selectedCurrency);
    setValue('currency', selectedCurrency, true);
  };

  const onSubmit = (data) => {
    dispatch(updateForm({ name: 'accidentInfo', data }));
    goNext(step);
  };

  return (
    <Box className={styles.formContainer}>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={showContractNotExistError}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, showContractNotExistError]}
      />
      <FormSection icon={AccidentInfoIcon} title="claim.accInfo.accInfo">
        <Grid container spacing={4}>
          <Grid item md={4} xs={12}>
            <DateInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.date"
              name="accidentDate"
              value={accDate || null}
              handleChange={(date) => {
                // eslint-disable-next-line no-restricted-globals
                if (date instanceof Date && !isNaN(date)) {
                  updateAccDate(date);
                }
              }}
              maxDate={new Date()}
              required
            />
            {!errors.accidentDate && showContractNotExistError && (
              <p className={styles.errorText}>
                {t('claim.accInfo.inSelectedDateContractNotExist')}
              </p>
            )}
          </Grid>
          <Grid item md={4} xs={12}>
            <TextInputFieldWithValidation
              required
              formProps={{
                errors,
                clearError,
              }}
              labelKey="claim.accInfo.damageAmount"
              name="damageAmount"
              handleChange={(event) => {
                const currentValue = event.target.value;
                const fixedValue = currentValue.replace(NON_DIGITS_PATTERN, '');
                updateDamageAmount(Number(fixedValue));
              }}
              type="tel"
              value={damageAmount ? damageAmount.toLocaleString() : ''}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <SelectWithValidation
              name="currency"
              required
              label="claim.accInfo.currency"
              value={currency || ''}
              formProps={{
                errors,
                clearError,
              }}
              getMenuLabel={(option) => option.code}
              options={currencies}
              onChange={(data) => handleCurrencyChange(data)}
            />
          </Grid>
          {accidentTimeComponent && (
            <Grid item xs={12} sm={4}>
              {
                React.cloneElement(accidentTimeComponent, {
                  accidentInfo,
                  formProps: { errors, clearError, register, setValue },
                })
              }
            </Grid>
          )}
        </Grid>
      </FormSection>
      {accidentAddressComponent && React.cloneElement(accidentAddressComponent, {
        accidentInfo,
        formProps: { errors, clearError, register, setValue, getValues },
        countries,
        claimType,
      })}
      {accDate && !errors.accidentDate && !showContractNotExistError && (
        <ExistingContracts
          existingContracts={existingContracts}
          updateContract={updateContract}
          errors={errors}
          contract={contract}
          classes={classes}
        />
      )}
      {serviceCenterComponent && React.cloneElement(serviceCenterComponent, {
        accidentInfo,
        setValue,
        step,
        formProps: { errors, clearError },
      })}
      <div className={styles.showAccInfoCheckboxBlock}>
        <MainSwitch
          color="primary"
          checked={openAccidentInfoBlock}
          onChange={() => setOpenAccidentInfoBlock(!openAccidentInfoBlock)}
        />
        <label className={styles.showAccInfoCheckbox}>
          <p>{t('claim.accInfo.additionalInformation')}</p>
        </label>
      </div>
      {openAccidentInfoBlock && (
        <div className={styles.accidentInformationBlock}>
          <Validation name="accidentInformation" errors={errors} clearError={clearError}>
            <TextAreaField
              color="primary"
              id="accidentInformation"
              name="accidentInformation"
              value={accidentInformation}
              onChange={(e) => updateAccidentInformation(e.target.value)}
              multiline
            />
          </Validation>
        </div>
      )}
    </Box>
  );
};

AccidentInfoStep.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.shape({})),
  formLanguage: PropTypes.string.isRequired,
  claim: PropTypes.shape({
    accidentInfo: PropTypes.shape({
      accidentDate: PropTypes.instanceOf(Date),
      damageAmount: PropTypes.number,
      contract: PropTypes.shape({}),
      accidentInformation: PropTypes.string,
      currency: PropTypes.shape({}),
    }),
  }).isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
  step: PropTypes.shape({
    claimForm: PropTypes.string.isRequired,
    n: PropTypes.number.isRequired,
  }).isRequired,
  updateForm: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  serviceCenterComponent: PropTypes.element,
  accidentAddressComponent: PropTypes.element,
  accidentTimeComponent: PropTypes.element,
  requiredFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  validationSchema: PropTypes.shape({}).isRequired,
  claimType: PropTypes.oneOf(Object.values(CLAIM_TYPES)),
};

AccidentInfoStep.defaultProps = {
  serviceCenterComponent: undefined,
  accidentAddressComponent: undefined,
  accidentTimeComponent: undefined,
  countries: [],
  claimType: null,
};

export default AccidentInfoStep;
